import type { LiqaError } from "@/errors"
import type { AnalyticsDetails } from "@/libs/telemetry"
import type { CaptureFormat, CaptureTransformations, ImageCapture } from "../capture"

export type { CaptureFormat, CaptureTransformations, ImageCapture, LiqaError }

/** LIQA event names. */
export const Events = {
  /** Fired when LIQA has finished loading its resources and is ready to operate. */
  READY: "ready" as const,
  /**
   * Fired when LIQA has finished loading its resources and is ready to operate.
   * @deprecated Use {@link Events.READY} instead.
   */
  LOADED: "loaded" as const,
  /**
   * Fired when all the AI quality criteria passed and the user submitted the image.
   * The image can be obtained via the event details {@link ImageCapture} interface.
   *
   * @deprecated Use {@link Events.CAPTURES} instead.
   */
  CAPTURE: "capture" as const,
  /**
   * Fired when all the AI quality criteria passed and the user submitted the image.
   * The images can be obtained via the event details {@link ImageCapture} interface.
   */
  CAPTURES: "captures" as const,
  /** Fired when an analytics event occur. */
  ANALYTICS: "analytics" as const,
  /** Fired when a {@link LiqaError} occurs. */
  ERROR: "error" as const,
} // TODO: try to use `as const`, check how it looks in API Reference

/** LIQA event names and corresponding event details. */
export type EventMap = {
  /** Fired when LIQA has finished loading its resources and is ready to operate. */
  [Events.READY]: void
  /**
   * Fired when LIQA has finished loading its resources and is ready to operate.
   * @alias `ready`
   * @deprecated Use the `ready` event instead.
   */
  [Events.LOADED]: void
  /**
   * Fired when all the AI quality criteria passed and the user submitted the image.
   * The image can be obtained via the `event.detail` {@link ImageCapture} interface.
   *
   * _Note: For_ `face-180` _preset this event is dispatched 3 times (for `front`, `left` and `right` side captures). The captured side is written to image metadata. An example how to handle it is on [Collection of Images](/getting-started/quickstart#multiple-captures) page._
   */
  [Events.CAPTURE]: ImageCapture
  /**
   * Fired when all the AI quality criteria passed and the user submitted the image.
   * The image can be obtained via the `event.detail` {@link ImageCapture} interface.
   */
  [Events.CAPTURES]: ImageCapture[]
  /** Fired when an analytics event occur. */
  [Events.ANALYTICS]: AnalyticsDetails
  /** Fired when a {@link LiqaError} occurs. */
  [Events.ERROR]: LiqaError
}
